import { log } from '../utils/logger';

/**
 * Reservations API module
 * Handles all reservation-related API operations
 */

import { db, supabase } from '../lib/supabase';
import { createAPIMethod } from './base';

/**
 * Reservation API namespace
 */
export const reservationAPI = {
  /**
   * Create a new reservation
   * @param {Object} data - Reservation data
   * @returns {Promise<APIResponse>}
   */
  create: createAPIMethod(
    (data) => db.createReservation(data),
    { 
      operation: 'Create reservation',
      transformData: (reservation) => {
        log.info('📝 New reservation created:', reservation);
        return reservation;
      }
    }
  ),

  /**
   * Update an existing reservation
   * @param {string} id - Reservation ID
   * @param {Object} data - Updated reservation data
   * @returns {Promise<APIResponse>}
   */
  update: createAPIMethod(
    (id, data) => db.updateReservation(id, data),
    { operation: 'Update reservation' }
  ),

  /**
   * Cancel a reservation
   * @param {string} id - Reservation ID
   * @returns {Promise<APIResponse>}
   */
  cancel: createAPIMethod(
    async (id) => {
      return await supabase
        .from('reservations')
        .update({ status: 'cancelled' })
        .eq('id', id)
        .select()
        .single();
    },
    { operation: 'Cancel reservation' }
  ),

  /**
   * Delete a reservation
   * @param {string} id - Reservation ID
   * @returns {Promise<APIResponse>}
   */
  delete: createAPIMethod(
    async (id) => {
      return await supabase
        .from('reservations')
        .delete()
        .eq('id', id);
    },
    { operation: 'Delete reservation' }
  ),

  /**
   * Get a single reservation by ID
   * @param {string} id - Reservation ID
   * @returns {Promise<APIResponse>}
   */
  getById: createAPIMethod(
    async (id) => {
      return await supabase
        .from('reservations')
        .select(`
          *,
          venues (
            id, name, address_line1, address_line2, 
            city, state, zip_code, phone, website
          ),
          teams (
            id, name
          ),
          games (
            id, starts_at, ends_at, timezone,
            home_team:teams!home_team_id (name),
            away_team:teams!away_team_id (name),
            game_locations (
              name, city, state, address_line1
            )
          ),
          reservation_backup_venues (
            id,
            venue_id,
            sort_order,
            venues (
              id, name, city, state, address_line1,
              venue_categories (
                id, name
              )
            )
          )
        `)
        .eq('id', id)
        .single();
    },
    { operation: 'Get reservation' }
  ),

  /**
   * Get all reservations with filters
   * @param {Object} filters - Filter options
   * @returns {Promise<APIResponse>}
   */
  getAll: createAPIMethod(
    async (filters = {}) => {
      let query = supabase
        .from('reservations')
        .select(`
          *,
          venues (
            id, name, address_line1, address_line2,
            city, state, zip_code, phone, website
          ),
          teams (
            id, name
          ),
          games (
            id, starts_at, ends_at, timezone,
            home_team:teams!home_team_id (name),
            away_team:teams!away_team_id (name),
            game_locations (
              name, city, state, address_line1
            )
          ),
          reservation_backup_venues (
            id,
            venue_id,
            sort_order,
            venues (
              id, name, city, state, address_line1,
              venue_categories (
                id, name
              )
            )
          )
        `);

      // Apply filters
      if (filters.userId) {
        query = query.eq('created_by', filters.userId);
      }
      if (filters.venueId) {
        query = query.eq('venue_id', filters.venueId);
      }
      if (filters.teamId) {
        query = query.eq('team_id', filters.teamId);
      }
      if (filters.status) {
        query = query.eq('status', filters.status);
      }
      if (filters.dateFrom) {
        query = query.gte('reserved_at', filters.dateFrom);
      }
      if (filters.dateTo) {
        query = query.lte('reserved_at', filters.dateTo);
      }

      // Add ordering
      query = query.order('reserved_at', { ascending: false });

      return await query;
    },
    { operation: 'Get reservations' }
  ),

  /**
   * Get reservations for a specific user
   * @param {string} userId - User ID
   * @returns {Promise<APIResponse>}
   */
  getByUser: createAPIMethod(
    async (userId) => {
      return await reservationAPI.getAll({ userId });
    },
    { operation: 'Get user reservations' }
  ),

  /**
   * Get reservations for a specific venue
   * @param {string} venueId - Venue ID
   * @returns {Promise<APIResponse>}
   */
  getByVenue: createAPIMethod(
    async (venueId) => {
      return await reservationAPI.getAll({ venueId });
    },
    { operation: 'Get venue reservations' }
  ),

  /**
   * Get reservations for a specific team
   * @param {string} teamId - Team ID
   * @returns {Promise<APIResponse>}
   */
  getByTeam: createAPIMethod(
    async (teamId) => {
      return await reservationAPI.getAll({ teamId });
    },
    { operation: 'Get team reservations' }
  ),

  /**
   * Update reservation status
   * @param {string} id - Reservation ID
   * @param {string} status - New status
   * @returns {Promise<APIResponse>}
   */
  updateStatus: createAPIMethod(
    async (id, status) => {
      const validStatuses = ['pending', 'confirmed', 'cancelled', 'completed'];
      if (!validStatuses.includes(status)) {
        throw new Error(`Invalid status: ${status}`);
      }

      return await supabase
        .from('reservations')
        .update({ 
          status,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();
    },
    { operation: 'Update reservation status' }
  ),

  /**
   * Add notes to a reservation
   * @param {string} id - Reservation ID
   * @param {string} notes - Notes to add
   * @returns {Promise<APIResponse>}
   */
  addNotes: createAPIMethod(
    async (id, notes) => {
      return await supabase
        .from('reservations')
        .update({ 
          notes,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();
    },
    { operation: 'Add reservation notes' }
  ),


  /**
   * Add backup venues to a reservation
   * @param {string} reservationId - Reservation ID
   * @param {Array} venueIds - Array of venue IDs
   * @returns {Promise<APIResponse>}
   */
  addBackupVenues: createAPIMethod(
    async (reservationId, venueIds) => {
      // First delete existing backup venues
      await supabase
        .from('reservation_backup_venues')
        .delete()
        .eq('reservation_id', reservationId);

      // Then add new backup venues
      const backupVenues = venueIds.map((venueId, index) => ({
        reservation_id: reservationId,
        venue_id: venueId,
        sort_order: index
      }));

      return await supabase
        .from('reservation_backup_venues')
        .insert(backupVenues)
        .select();
    },
    { operation: 'Add backup venues' }
  ),

  /**
   * Change the venue for a reservation
   * @param {string} id - Reservation ID
   * @param {string} newVenueId - New venue ID
   * @param {string} changeReason - Reason for venue change
   * @returns {Promise<APIResponse>}
   */
  changeVenue: createAPIMethod(
    async (id, newVenueId, changeReason) => {
      // Get current reservation to store original venue
      const { data: currentReservation } = await supabase
        .from('reservations')
        .select('venue_id, notes')
        .eq('id', id)
        .single();

      if (!currentReservation) {
        throw new Error('Reservation not found');
      }

      // Update notes with venue change information
      const venueChangeNote = {
        venue_change: {
          from: currentReservation.venue_id,
          to: newVenueId,
          reason: changeReason,
          changed_at: new Date().toISOString()
        }
      };

      let updatedNotes;
      try {
        const existingNotes = currentReservation.notes 
          ? JSON.parse(currentReservation.notes) 
          : {};
        updatedNotes = JSON.stringify({
          ...existingNotes,
          ...venueChangeNote
        });
      } catch {
        updatedNotes = JSON.stringify(venueChangeNote);
      }

      return await supabase
        .from('reservations')
        .update({
          venue_id: newVenueId,
          notes: updatedNotes,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();
    },
    { operation: 'Change reservation venue' }
  ),

  /**
   * Check if a reservation can be cancelled (24 hour rule)
   * @param {string} id - Reservation ID
   * @returns {Promise<APIResponse>}
   */
  canCancel: createAPIMethod(
    async (id) => {
      const { data: reservation } = await supabase
        .from('reservations')
        .select('reserved_at, status')
        .eq('id', id)
        .single();

      if (!reservation) {
        return { canCancel: false, reason: 'Reservation not found' };
      }

      if (reservation.status === 'cancelled') {
        return { canCancel: false, reason: 'Reservation already cancelled' };
      }

      const reservationTime = new Date(reservation.reserved_at);
      const now = new Date();
      const hoursUntilReservation = (reservationTime - now) / (1000 * 60 * 60);

      if (hoursUntilReservation < 24) {
        return { 
          canCancel: false, 
          reason: 'Cannot cancel within 24 hours of reservation time' 
        };
      }

      return { canCancel: true };
    },
    { operation: 'Check cancellation eligibility' }
  ),

  /**
   * Batch update multiple reservations
   * @param {Array} updates - Array of {id, data} objects
   * @returns {Promise<APIResponse>}
   */
  batchUpdate: createAPIMethod(
    async (updates) => {
      const results = await Promise.allSettled(
        updates.map(({ id, data }) => 
          supabase
            .from('reservations')
            .update(data)
            .eq('id', id)
            .select()
            .single()
        )
      );

      return {
        successful: results.filter(r => r.status === 'fulfilled').map(r => r.value),
        failed: results.filter(r => r.status === 'rejected').map((r, i) => ({
          id: updates[i].id,
          error: r.reason
        }))
      };
    },
    { operation: 'Batch update reservations' }
  ),

  /**
   * Get backup venues for a reservation
   * @param {string} reservationId - Reservation ID
   * @returns {Promise<APIResponse>}
   */
  getBackupVenues: createAPIMethod(
    async (reservationId) => {
      return await supabase
        .from('reservation_backup_venues')
        .select(`
          id,
          venue_id,
          venues (
            id, name, city, state, address_line1,
            venue_categories (
              id, name
            ),
            venue_cuisines:cuisine_id (
              id, category, subcategory
            )
          )
        `)
        .eq('reservation_id', reservationId)
        .order('created_at', { ascending: true });
    },
    { operation: 'Get reservation backup venues' }
  )
};